import React, { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowRight, TrendingUp, Rocket, GraduationCap, Trophy } from "lucide-react";
import PageHero from "@/components/shared/PageHero";
import { useLang } from "@/i18n/LanguageContext";
import { SUBSIDIARIES } from "@/data/ekipData";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PILLAR_ICONS = [Rocket, TrendingUp, GraduationCap, Trophy];

const HERO_IMG =
  "https://images.pexels.com/photos/32168739/pexels-photo-32168739.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=800&w=1600";

export default function Carrieres() {
  const { t, lang } = useLang();
  const [entity, setEntity] = useState("all");
  const [type, setType] = useState("all");
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    subsidiary: "",
    motivation: "",
    cvFileName: "",
    cvBase64: "",
  });
  const [loading, setLoading] = useState(false);

  const filtered = t.carrieres.offers.items.filter(
    (o) => (entity === "all" || o.entity === entity) && (type === "all" || o.type === type)
  );

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setForm((f) => ({ ...f, cvFileName: file.name, cvBase64: reader.result }));
    };
    reader.readAsDataURL(file);
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/careers/apply`, {
        firstName: form.firstName,
        lastName: form.lastName,
        email: form.email,
        phone: form.phone,
        subsidiary: form.subsidiary,
        motivation: form.motivation,
        cvFileName: form.cvFileName,
        cvBase64: form.cvBase64,
        lang,
      });
      toast.success(t.common.success);
      setForm({
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        subsidiary: "",
        motivation: "",
        cvFileName: "",
        cvBase64: "",
      });
    } catch {
      toast.error(t.common.error);
    } finally {
      setLoading(false);
    }
  };

  const entities = ["all", ...SUBSIDIARIES.map((s) => s.name), "Holding"];
  const types = ["all", "CDI", "CDD", "Stage"];

  return (
    <div data-testid="carrieres-page">
      <PageHero
        overline={t.carrieres.hero.overline}
        title={t.carrieres.hero.title}
        titleItalic={t.carrieres.hero.titleItalic}
        subtitle={t.carrieres.hero.subtitle}
        image={HERO_IMG}
      />

      {/* Why EKIP */}
      <section className="bg-ekip-cream py-20 md:py-32" data-testid="carrieres-why">
        <div className="mx-auto max-w-[1440px] px-6 md:px-10 lg:px-14">
          <div className="grid grid-cols-12 gap-6 md:gap-10 mb-16">
            <div className="col-span-12 md:col-span-4 flex items-start gap-3">
              <span className="diamond mt-2" />
              <span className="text-[11px] uppercase tracking-[0.35em] text-ekip-copper font-medium">
                {t.carrieres.why.eyebrow}
              </span>
            </div>
            <h2 className="col-span-12 md:col-span-8 font-display font-semibold text-3xl md:text-5xl leading-[1.05] text-ekip-navy">
              {t.carrieres.why.title}
              <br />
              <em className="italic font-light text-ekip-copper">{t.carrieres.why.titleItalic}</em>
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 border-t border-l border-ekip-gold/20">
            {t.carrieres.why.pillars.map((p, i) => {
              const Icon = PILLAR_ICONS[i];
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{ duration: 0.6, delay: i * 0.08 }}
                  className="p-8 md:p-10 border-r border-b border-ekip-gold/20 bg-white hover:bg-ekip-cream transition-colors"
                >
                  <div className="w-11 h-11 flex items-center justify-center border border-ekip-copper/50 mb-8" style={{ transform: "rotate(45deg)" }}>
                    <Icon size={18} strokeWidth={1.4} className="text-ekip-copper" style={{ transform: "rotate(-45deg)" }} />
                  </div>
                  <h3 className="font-display font-semibold text-lg md:text-xl text-ekip-navy leading-snug">
                    {p.title}
                  </h3>
                  <p className="mt-3 text-sm text-ekip-graphite font-body font-light leading-relaxed">
                    {p.desc}
                  </p>
                  <span className="absolute top-6 right-6 text-[10px] tracking-[0.3em] text-ekip-copper/50">
                    0{i + 1}
                  </span>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Offers */}
      <section className="bg-white py-20 md:py-32 border-y border-ekip-gold/20" data-testid="carrieres-offers">
        <div className="mx-auto max-w-[1440px] px-6 md:px-10 lg:px-14">
          <div className="grid grid-cols-12 gap-6 md:gap-10 mb-14">
            <div className="col-span-12 md:col-span-4 flex items-start gap-3">
              <span className="diamond mt-2" />
              <span className="text-[11px] uppercase tracking-[0.35em] text-ekip-copper font-medium">
                {t.carrieres.offers.eyebrow}
              </span>
            </div>
            <h2 className="col-span-12 md:col-span-8 font-display font-semibold text-3xl md:text-5xl leading-[1.05] text-ekip-navy">
              {t.carrieres.offers.title}
              <br />
              <em className="italic font-light text-ekip-copper">{t.carrieres.offers.titleItalic}</em>
            </h2>
          </div>

          {/* Filters */}
          <div className="flex flex-wrap gap-6 mb-8">
            <div>
              <label className="text-[10px] uppercase tracking-[0.28em] text-ekip-graphite">
                {t.carrieres.offers.filterEntity}
              </label>
              <div className="mt-2 flex flex-wrap gap-2">
                {entities.map((e) => (
                  <button
                    key={e}
                    onClick={() => setEntity(e)}
                    data-testid={`careers-entity-${e}`}
                    className={`text-[11px] uppercase tracking-[0.22em] font-medium px-3 py-1.5 border transition-colors ${
                      entity === e
                        ? "bg-ekip-navy text-white border-ekip-navy"
                        : "bg-white text-ekip-navy border-ekip-gold/30 hover:border-ekip-copper"
                    }`}
                  >
                    {e === "all" ? t.carrieres.offers.all : e}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.28em] text-ekip-graphite">
                {t.carrieres.offers.filterType}
              </label>
              <div className="mt-2 flex flex-wrap gap-2">
                {types.map((tp) => (
                  <button
                    key={tp}
                    onClick={() => setType(tp)}
                    data-testid={`careers-type-${tp}`}
                    className={`text-[11px] uppercase tracking-[0.22em] font-medium px-3 py-1.5 border transition-colors ${
                      type === tp
                        ? "bg-ekip-navy text-white border-ekip-navy"
                        : "bg-white text-ekip-navy border-ekip-gold/30 hover:border-ekip-copper"
                    }`}
                  >
                    {tp === "all" ? t.carrieres.offers.all : tp}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="divide-y divide-ekip-gold/20 border-t border-b border-ekip-gold/20">
            {filtered.map((o, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -10 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.04 }}
                data-testid={`career-offer-${i}`}
                className="grid grid-cols-1 md:grid-cols-12 gap-4 py-6 items-center hover:bg-ekip-cream transition-colors px-2"
              >
                <div className="md:col-span-5">
                  <h3 className="font-display font-semibold text-lg md:text-xl text-ekip-navy">
                    {o.title}
                  </h3>
                  <span className="mt-1 block text-xs text-ekip-graphite">{o.location} · {o.date}</span>
                </div>
                <div className="md:col-span-3">
                  <span className="text-[10px] uppercase tracking-[0.24em] text-ekip-copper font-medium">
                    {o.entity}
                  </span>
                </div>
                <div className="md:col-span-2">
                  <span className="inline-block text-[11px] uppercase tracking-[0.22em] font-medium bg-ekip-cream border border-ekip-gold/30 px-3 py-1">
                    {o.type}
                  </span>
                </div>
                <div className="md:col-span-2 md:text-right">
                  <a
                    href="#candidature"
                    className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.24em] text-ekip-copper font-medium hover:text-ekip-navy transition-colors"
                  >
                    {t.carrieres.offers.apply}
                    <ArrowRight size={13} />
                  </a>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Spontaneous application */}
      <section id="candidature" className="bg-ekip-navy py-20 md:py-32" data-testid="carrieres-form">
        <div className="mx-auto max-w-[1440px] px-6 md:px-10 lg:px-14">
          <div className="grid grid-cols-12 gap-6 md:gap-14">
            <div className="col-span-12 md:col-span-4">
              <span className="text-[11px] uppercase tracking-[0.35em] text-ekip-copper font-medium flex items-center gap-3">
                <span className="diamond" />
                {t.carrieres.spontaneous.eyebrow}
              </span>
              <h2 className="mt-6 font-display font-semibold text-3xl md:text-4xl text-white leading-[1.05]">
                {t.carrieres.spontaneous.title}
                <br />
                <em className="italic font-light text-ekip-copper">{t.carrieres.spontaneous.titleItalic}</em>
              </h2>
              <p className="mt-6 text-white/70 font-body font-light leading-relaxed max-w-md">
                {t.carrieres.spontaneous.subtitle}
              </p>
            </div>

            <form onSubmit={submit} className="col-span-12 md:col-span-8 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6" data-testid="carrieres-form-el">
              <div>
                <input
                  required
                  type="text"
                  placeholder={t.carrieres.spontaneous.firstName}
                  value={form.firstName}
                  onChange={(e) => setForm({ ...form, firstName: e.target.value })}
                  data-testid="careers-input-firstName"
                  className="form-input form-input-dark"
                />
              </div>
              <div>
                <input
                  required
                  type="text"
                  placeholder={t.carrieres.spontaneous.lastName}
                  value={form.lastName}
                  onChange={(e) => setForm({ ...form, lastName: e.target.value })}
                  data-testid="careers-input-lastName"
                  className="form-input form-input-dark"
                />
              </div>
              <div>
                <input
                  required
                  type="email"
                  placeholder={t.carrieres.spontaneous.email}
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  data-testid="careers-input-email"
                  className="form-input form-input-dark"
                />
              </div>
              <div>
                <input
                  type="tel"
                  placeholder={t.carrieres.spontaneous.phone}
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  data-testid="careers-input-phone"
                  className="form-input form-input-dark"
                />
              </div>
              <div className="md:col-span-2">
                <select
                  required
                  value={form.subsidiary}
                  onChange={(e) => setForm({ ...form, subsidiary: e.target.value })}
                  data-testid="careers-input-subsidiary"
                  className="form-input form-input-dark bg-ekip-navy"
                >
                  <option value="" disabled>{t.carrieres.spontaneous.subsidiary}</option>
                  {SUBSIDIARIES.map((s) => (
                    <option key={s.id} value={s.name}>{s.name}</option>
                  ))}
                  <option value="Holding">Holding</option>
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="text-[10px] uppercase tracking-[0.28em] text-white/60">
                  {t.carrieres.spontaneous.cvLabel}
                </label>
                <div className="mt-3 flex items-center gap-4">
                  <label className="inline-flex items-center gap-2 border border-ekip-copper/50 text-ekip-copper px-4 py-2 text-[11px] uppercase tracking-[0.22em] cursor-pointer hover:bg-ekip-copper hover:text-white transition-colors">
                    {t.carrieres.spontaneous.cvChoose}
                    <input
                      type="file"
                      accept=".pdf,.doc,.docx"
                      onChange={handleFile}
                      className="hidden"
                      data-testid="careers-input-cv"
                    />
                  </label>
                  <span className="text-sm text-white/60">
                    {form.cvFileName || "—"}
                  </span>
                </div>
              </div>
              <div className="md:col-span-2">
                <textarea
                  required
                  rows={4}
                  placeholder={t.carrieres.spontaneous.motivation}
                  value={form.motivation}
                  onChange={(e) => setForm({ ...form, motivation: e.target.value })}
                  data-testid="careers-input-motivation"
                  className="form-input form-input-dark resize-none"
                />
              </div>
              <div className="md:col-span-2 mt-4">
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="careers-submit"
                  className="btn-primary disabled:opacity-60"
                >
                  {loading ? t.common.submitting : t.carrieres.spontaneous.submit}
                  <ArrowRight size={14} />
                </button>
              </div>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}
